import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faCalendarDays, faPenToSquare } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import * as func from "../../api/apiHelper";
import * as urls from "../../api/urlHelper";

const STATUS_MAP = {
  draft: { cls: "bg-gray-100 text-gray-600", label: "Draft" },
  pending_approval: { cls: "bg-amber-100 text-amber-700", label: "Pending Approval" },
  approved: { cls: "bg-green-100 text-green-700", label: "Approved" },
  live: { cls: "bg-blue-100 text-blue-700", label: "Live" },
  closed: { cls: "bg-gray-200 text-gray-500", label: "Closed" },
  cancelled: { cls: "bg-red-100 text-red-600", label: "Cancelled" },
};

const Stat = ({ label, value, sub, bad }) => (
  <div className={`rounded-lg border px-3 py-2 ${bad ? "border-amber-200 bg-amber-50" : "border-gray-100 bg-gray-50"}`}>
    <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">{label}</p>
    <p className={`text-base font-semibold ${bad ? "text-amber-700" : "text-gray-800"}`}>{value}</p>
    {sub && <p className="text-[10px] text-gray-400 mt-0.5">{sub}</p>}
  </div>
);

const Field = ({ label, value }) => (
  <div>
    <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">{label}</p>
    <p className="text-sm text-gray-800 mt-0.5">{value ?? "—"}</p>
  </div>
);

const ViewEvent = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ev, setEv] = useState(null);
  const [loading, setLoading] = useState(true);
  // The room, not just the event record: who registered, whether each of them
  // can actually be scored and get through the door, and what would break
  // tonight. Loaded alongside the event so this screen answers "is this ready?"
  // rather than only "what did we type in?".
  const [room, setRoom] = useState(null);
  const [building, setBuilding] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await func.get(`${urls.GET_EVENT_BY_ID_URL}${id}`);
        if (res?.status === 200 && res?.data?.event) {
          setEv(res.data.event);
          loadRoom();
        } else {
          toast.error("Event not found");
          navigate("/events/list");
        }
      } catch (err) {
        toast.error("Failed to load event");
        navigate("/events/list");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const loadRoom = async () => {
    try {
      const r = await func.get(`${urls.EVENT_ROSTER_URL}${id}/roster`);
      if (r?.status === 200 && r?.data?.success) setRoom(r.data);
    } catch (err) {
      // The room view is additive — its failure must never blank the event page.
      console.error("roster load failed", err);
    }
  };

  // The step that used to be a terminal and a person who knew which script.
  const compute = async () => {
    setBuilding(true);
    try {
      const r = await func.post(`${urls.BUILD_EVENT_MATRIX_URL}${id}/build-matrix`, {});
      if (r?.status === 200 && r?.data?.success) {
        const d = r.data;
        toast.success(`Matrix built — ${d.stored} pair${d.stored === 1 ? "" : "s"} scored, ${d.refused} refused by the walls`);
        await loadRoom();
      } else {
        toast.warn(r?.data?.message || "Could not build the matrix");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not build the matrix");
    } finally {
      setBuilding(false);
    }
  };

  if (loading || !ev) {
    return (
      <div className="min-h-screen bg-[linear-gradient(to_left,_#19224B,_#E2B865)] p-6 flex items-center justify-center">
        <p className="text-white/70 text-sm">Loading…</p>
      </div>
    );
  }

  const st = STATUS_MAP[ev.status] || { cls: "bg-gray-100 text-gray-500", label: ev.status };

  return (
    <div className="min-h-screen bg-[linear-gradient(to_left,_#19224B,_#E2B865)] p-6">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="w-8 h-8 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-lg transition text-white"
            >
              <FontAwesomeIcon icon={faChevronLeft} className="text-sm" />
            </button>
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faCalendarDays} className="text-white" />
              <h1 className="text-xl font-bold text-white">{ev.publicLabel}</h1>
            </div>
          </div>
          <button
            onClick={() => navigate(`/events/edit/${ev._id}`)}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-white text-indigo-700 font-semibold rounded-lg hover:bg-indigo-50 transition shadow"
          >
            <FontAwesomeIcon icon={faPenToSquare} />
            Edit
          </button>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6 space-y-6">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-400">matchmatrix.in/e/{ev.slug}</p>
            <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${st.cls}`}>{st.label}</span>
          </div>

          <section className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100">
            <Field label="Internal name" value={ev.name} />
            <Field label="Starts" value={ev.startAt ? new Date(ev.startAt).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : null} />
            <Field label="Duration" value={ev.durationMin ? `${ev.durationMin} min` : null} />
            <Field
              label="Matrix built"
              value={ev.matrixBuiltAt
                ? <>
                    {new Date(ev.matrixBuiltAt).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                    {room?.event?.matrixStale && (
                      <span className="text-amber-600"> · stale, people registered since</span>
                    )}
                  </>
                : <span className="text-red-500">Not built — every guest would see an empty room</span>}
            />
          </section>

          <section className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-100">
            <Field label="City" value={ev.city} />
            <Field label="Community" value={ev.community} />
            <Field label="Profession tag" value={ev.professionTag} />
            <Field label="Marital pool" value={ev.maritalPool === "second_marriage" ? "Second marriage" : "Never married"} />
          </section>

          <section className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-100">
            <Field label="Capacity (presenters)" value={ev.capacity?.presenters} />
            <Field label="Max gender ratio" value={ev.maxGenderRatio} />
            <Field label="Base price" value={ev.basePrice != null ? `₹${ev.basePrice}` : null} />
            <Field label="Women free" value={ev.womenFree ? "Yes" : "No"} />
          </section>


          {/* ── THE ROOM ───────────────────────────────────────────────────────
              Everything above describes the event RECORD. This describes the
              actual room: how full it is, whether the money arrived, whether
              each guest can be scored and get through the door, and the one
              button that has to be pressed before any of it works. */}
          <section className="pt-4 border-t border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">The room</h3>
              <button
                onClick={compute}
                disabled={building}
                className="px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 transition shadow"
              >
                {building ? "Computing…" : ev.matrixBuiltAt ? "Recompute scores" : "Compute scores"}
              </button>
            </div>

            {!room ? (
              <p className="text-sm text-gray-400">Loading the room…</p>
            ) : (
              <>
                {/* What would go wrong tonight, in the order it would go wrong. */}
                {room.blockers?.length > 0 && (
                  <div className="mb-4 space-y-2">
                    {room.blockers.map((b) => (
                      <div
                        key={b.code}
                        className={`rounded-lg px-3 py-2 text-sm border ${
                          b.severity === "stop"
                            ? "bg-red-50 border-red-200 text-red-700"
                            : "bg-amber-50 border-amber-200 text-amber-800"
                        }`}
                      >
                        <b className="font-semibold">{b.severity === "stop" ? "Stop" : "Warn"}</b> · {b.note}
                      </div>
                    ))}
                  </div>
                )}
                {room.blockers?.length === 0 && (
                  <div className="mb-4 rounded-lg px-3 py-2 text-sm bg-green-50 border border-green-200 text-green-700">
                    <b className="font-semibold">Ready</b> · nothing is standing between this room and a working evening.
                  </div>
                )}

                <div className="grid grid-cols-4 gap-3 mb-4">
                  <Stat label="Seats held" value={`${room.counts.held} / ${room.event.capacity}`} />
                  <Stat label="Men · Women" value={`${room.counts.male} · ${room.counts.female}`}
                        bad={!room.counts.male || !room.counts.female} />
                  <Stat label="Paid" value={`₹${room.counts.revenue}`} sub={`${room.counts.paid} paid · ${room.counts.freeSeats} free`} />
                  <Stat label="Waitlist" value={room.counts.waitlist} />
                  <Stat label="Profile complete" value={`${room.counts.ready} / ${room.counts.held}`}
                        bad={room.counts.ready < room.counts.held} />
                  <Stat label="Birth details" value={`${room.counts.withBirthDetails} / ${room.counts.held}`}
                        bad={room.counts.withBirthDetails < room.counts.held} />
                  <Stat label="Pairs scored" value={room.matrix.pairs} bad={!room.matrix.pairs} />
                  <Stat label="Strong pairs" value={room.matrix.strongPairs} />
                </div>

                <div className="overflow-x-auto border border-gray-100 rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-500">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium">Guest</th>
                        <th className="text-left px-3 py-2 font-medium">Seat</th>
                        <th className="text-left px-3 py-2 font-medium">Paid</th>
                        <th className="text-left px-3 py-2 font-medium">Onboarding</th>
                        <th className="text-left px-3 py-2 font-medium">Birth details</th>
                        <th className="text-left px-3 py-2 font-medium">App</th>
                      </tr>
                    </thead>
                    <tbody>
                      {room.roster.map((r) => (
                        <tr key={r.userId} className={`border-t border-gray-100 ${r.holdsSeat ? "" : "opacity-45"}`}>
                          <td className="px-3 py-2">
                            <span className="font-medium text-gray-800">{r.name || "—"}</span>
                            <span className="text-gray-400"> · {r.gender || "?"}</span>
                            {r.city && <span className="text-gray-400"> · {r.city}</span>}
                            {r.viaMatchmaker && <span className="ml-1 text-xs text-indigo-500">via matchmaker</span>}
                          </td>
                          <td className="px-3 py-2 text-gray-600">{r.status}</td>
                          <td className="px-3 py-2 text-gray-600">
                            {r.freeSeat ? <span className="text-gray-400">free</span> : r.amountPaid > 0 ? `₹${r.amountPaid}` : <span className="text-amber-600">unpaid</span>}
                          </td>
                          <td className="px-3 py-2">
                            {r.profileComplete
                              ? <span className="text-green-600">complete</span>
                              : <span className="text-red-500">step {r.onboardingStep ?? 0}/16</span>}
                          </td>
                          <td className="px-3 py-2">
                            {r.hasBirthDetails ? <span className="text-green-600">yes</span> : <span className="text-amber-600">missing</span>}
                          </td>
                          <td className="px-3 py-2">
                            {r.hasApp ? <span className="text-green-600">yes</span> : <span className="text-gray-400">no</span>}
                          </td>
                        </tr>
                      ))}
                      {!room.roster.length && (
                        <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-400">Nobody has registered yet.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>
          <section className="pt-4 border-t border-gray-100">
            <Field
              label="Host"
              value={ev.hostMatchmakerId?._phoneNumber
                ? ev.hostMatchmakerId._phoneNumber
                : <span className="text-red-500">No host set — the roster, presenter and attendance endpoints will 403 for everyone until one is assigned</span>}
            />
          </section>

          <section className="pt-4 border-t border-gray-100 text-xs text-gray-400 flex justify-between">
            <span>Created {new Date(ev.createdAt).toLocaleDateString("en-IN")}</span>
            <span>Updated {new Date(ev.updatedAt).toLocaleDateString("en-IN")}</span>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ViewEvent;
